/**
 * Custom hook for managing shipments with API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { shipmentsAPI, customersAPI } from '@/lib/api';
import { useToast } from '@/hooks/useToast';

const API_BASE_URL = 'https://sourcemygoods.com/api/public';

// Helper function to send emails
async function sendEmail(endpoint: string, data: any) {
  try {
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      console.error(`Failed to send ${endpoint}:`, await response.text());
    }
  } catch (error) {
    console.error(`Error sending ${endpoint}:`, error);
  }
}

export function useShipments() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading, error } = useQuery({
    queryKey: ['shipments'],
    queryFn: async () => {
      const response = await shipmentsAPI.getAll();
      return response.data || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      // First create the shipment
      const shipmentResponse = await shipmentsAPI.create(data);
      const shipmentNumber = shipmentResponse.data?.shipment_number;
      
      // If shipment has items, save them
      if (data.items && data.items.length > 0 && shipmentNumber) {
        const itemsAPI = {
          create: (itemData: any) => fetch('https://sourcemygoods.com/api/controllers/shipment-items.php', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('shiphub-user') ? JSON.parse(localStorage.getItem('shiphub-user')!).token : ''}`
            },
            body: JSON.stringify(itemData)
          }).then(res => res.json())
        };
        
        // Save each item
        for (let i = 0; i < data.items.length; i++) {
          const item = data.items[i];
          await itemsAPI.create({
            shipment_number: shipmentNumber,
            item_number: i + 1,
            description: item.description,
            quantity: item.quantity,
            quantity_unit: item.quantityUnit || 'pcs',
            weight: item.weight,
            volume: item.volume,
            boxes: item.boxes
          });
        }
      }
      
      return shipmentResponse;
    },
    onSuccess: async (response: any, variables: any) => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      toast({ title: 'Success', description: 'Shipment created successfully' });

      // Send email notification
      try {
        // Fetch customer email
        const customersResponse = await customersAPI.getAll();
        const customers = customersResponse.data || [];
        const customer = customers.find((c: any) => c.code === variables.customer_code);

        if (customer && customer.email) {
          const shipmentNumber = response.data?.shipment_number || 'N/A';
          
          await sendEmail('send-shipment-created-email.php', {
            to: customer.email,
            customerName: customer.name || 'Valued Customer',
            shipmentData: {
              shipmentNumber: shipmentNumber,
              originWarehouse: variables.origin_warehouse || 'N/A',
              destinationWarehouse: variables.destination_warehouse || 'N/A',
              freightMethod: variables.freight_method || 'Air',
              weight: variables.weight || 0,
              volume: variables.volume || 0,
              packages: variables.packages || 0,
              estimatedDelivery: variables.estimated_delivery || '',
              tripName: variables.trip_name || 'N/A',
              description: variables.description || '',
            },
          });
        }
      } catch (emailError) {
        console.error('Failed to send shipment created email:', emailError);
        // Don't show error to user - email is optional
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: shipmentsAPI.update,
    onSuccess: async (response: any, variables: any) => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      toast({ title: 'Success', description: 'Shipment updated successfully' });

      // Send email if status changed
      if (variables.status) {
        try {
          // Wait for database to sync
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Get the shipment to find customer code
          const shipmentsResponse = await shipmentsAPI.getAll();
          const shipments = shipmentsResponse.data || [];
          
          const shipment = shipments.find((s: any) => 
            s.shipment_number === variables.shipment_number || 
            s.shipmentNumber === variables.shipment_number
          );

          if (shipment) {
            // Fetch customer email
            const customersResponse = await customersAPI.getAll();
            const customers = customersResponse.data || [];
            
            const customer = customers.find((c: any) => 
              c.code === shipment.customerCode || c.code === shipment.customer_code
            );

            if (customer && customer.email) {
              // Send shipment status update email with full details
              await sendEmail('send-shipment-status-email.php', {
                to: customer.email,
                customerName: customer.name || 'Valued Customer',
                shipmentNumber: variables.shipment_number,
                newStatus: variables.status,
                freightMethod: shipment.freight_method || shipment.freightMethod,
                origin: shipment.origin_warehouse || shipment.originWarehouse,
                destination: shipment.destination_warehouse || shipment.destinationWarehouse,
                weight: shipment.weight || 0,
                volume: shipment.volume || 0,
                packages: shipment.packages || 0,
                description: shipment.description || '',
                tripName: shipment.trip_name || shipment.tripName || '',
              });
            }
          }
        } catch (emailError) {
          console.error('Failed to send shipment status email:', emailError);
        }
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: shipmentsAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      toast({ title: 'Success', description: 'Shipment deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  return {
    shipments: data || [],
    isLoading,
    error,
    createShipment: createMutation.mutate,
    createShipmentAsync: createMutation.mutateAsync,
    updateShipment: updateMutation.mutate,
    deleteShipment: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
